import { Field, FieldName, FieldValue } from 'components/shared/Field';
import { FlexItem } from 'components/shared/FlexLayout';
import { formatDateFromM3 } from 'functions/dateFormatter';
import { FC } from 'react';
import { Analysis, Sample } from 'types';

import { styled as MUIStyled } from '@mui/system';


const StyledFieldName = MUIStyled(FieldName)(() => ({
  marginBottom: '0px',
}));

const StyledFieldValue = MUIStyled(FieldValue)(() => ({
  marginBottom: '0px',
}));

type StyledProps = {
  fontWeight?: boolean | React.CSSProperties['fontWeight'];
  color?: boolean | React.CSSProperties['color'];
};

type ArticleInfoProps = {
  /** Name of the field */
  name?: string;
  /** Value of the field */
  value?: string;
  /** Style of the field */
  styled?: StyledProps;
  /** Calibration of the field */
  calibration?: string;

  /** JSX element to render instead of value */
  jsxElement?: JSX.Element;
};

/**
 * @description Component to display sample information in a structured format.
 * @param calibration {string} - Calibration information to display in bold.
 * @param nameStyled {StyledProps} - Style properties for the field name.
 * @param valueStyled {StyledProps} - Style properties for the field value.
 */
const SampleInfo = ({ calibration, name, value, jsxElement }: ArticleInfoProps) => {
  return (
    <Field>
      {calibration !== '' && calibration !== undefined ? <b>{calibration}</b> : ''}
      <StyledFieldName>{<>{name}</>}</StyledFieldName>
      <StyledFieldValue>{jsxElement ?? <p>{value}</p>}</StyledFieldValue>
    </Field>
  );
};

type SampleInformationProps = typeof FlexItem.defaultProps & {
  analysis: Analysis | undefined;
  sample: Sample | undefined;
};

/**
 * @description Component to display sample information in a flex layout.
 * @param analysis {Analysis | undefined} - The analysis object containing calibration information.
 * @param sample {Sample | undefined} - The sample object containing various sample details.
 * @param style {React.CSSProperties} - Optional style object to apply to the FlexItem.
 * @returns
 */
export const SampleInformation: FC<SampleInformationProps> = ({ analysis, sample, style }) => {
  const flexItemStyle = { ...style };
  return (
    <FlexItem width={50} spacing={15} style={flexItemStyle}>
      <SampleInfo
        calibration={analysis?.calibration}
        name={sample?.sampleType}
        value={sample?.grainType}
      />
      <SampleInfo name="Anmodan skapad:" value={formatDateFromM3(sample?.registrationDate)} />
      <SampleInfo name="Lab:" value={sample?.lab} />
      <SampleInfo name="Analyspaket:" value={sample?.analysisPackage} />
      <SampleInfo name="Artikelnamn:" jsxElement={<b>{sample?.articleName}</b>} />
    </FlexItem>
  );
};
